import usePatchCommissioningData from '@/api/mutations/connectionApplication/usePatchCommissioningData';
import useSubmitCommissioningInfo from '@/api/mutations/connectionApplication/useSubmitCommissioningInfo';
import {
  FormAndJSONSchema,
  FormSection,
} from '@/api/rest/connectionApplication/useGetConnectionApplicationSchema';
import EnaFormFooter from '@/ui/components/enaForms/EnaFormFooter';
import { generateFormSectionsForScrollSpy } from '@/ui/components/enaForms/utils/display';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToastContext } from '@payaca/ambrosia';

import { JSONSchemaType } from 'ajv';
import { merge } from 'lodash-es';
import { FC, useMemo } from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import z from 'zod/v4';
import {
  generateJsonSchemaResolver,
  usePersistFormChanges,
} from '../../components/enaForms/utils/reactHookForm';
import CommissioningLayout from './CommissioningLayout';
import { ConnectionApplicationDataSchemaForCommissioning } from './connectionApplicationDataSchema';

export interface Props {
  jsonSchema: any;
  formSchema: FormAndJSONSchema;
  subSections: [string, FormSection][];
  projectId: string;
  connectionApplication: {
    id: string;
    data: Record<string, unknown>;
    commissioning?: {
      data: Record<string, unknown>;
      form: { class: string; version: number; schema: unknown };
      submittedAt?: string | null;
    } | null;
  };
}

const CommissioningForm: FC<Props> = (props) => {
  const {
    jsonSchema,
    formSchema,
    subSections,
    projectId,
    connectionApplication,
  } = props;

  /**
   * State
   */
  const history = useHistory();
  const { pushSuccess } = useToastContext();

  /**
   * Mutations
   */
  const { mutateAsync: patchCommissioningData } = usePatchCommissioningData();

  const mutationToSubmit = useSubmitCommissioningInfo({
    onSuccess: (result) => {
      if (result.__typename === 'CommissioningInfoSubmitted') {
        pushSuccess('Commissioning information submitted successfully.');
        history.push(`/projects/${projectId}/property`);
      }
    },
  });

  const { initialValues, initialisationError } = useMemo(
    () => getInitialFormValues(),
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'getInitialFormValues'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: formSchema
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: connectionApplication.commissioning
    [connectionApplication.commissioning, formSchema]
  );

  const form = useForm({
    mode: 'onChange',
    defaultValues: initialValues,
    resolver: generateFormResolver(jsonSchema),
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { isDirty },
  } = form;

  const hasChanges = usePersistFormChanges({
    control,
    persistedFormValues: connectionApplication.commissioning?.data,
    persistChanges: async (updates) => {
      await patchCommissioningData({
        connectionApplicationPublicId: connectionApplication.id,
        updates,
      });
    },
  });

  let errorMessage: string | undefined = undefined;

  if (initialisationError) {
    errorMessage = initialisationError;
  } else if (mutationToSubmit.isError) {
    errorMessage =
      'An error occurred while submitting the form. Please try again.';
  } else if (
    mutationToSubmit.data?.__typename === 'CommissioningInfoRejected'
  ) {
    errorMessage = mutationToSubmit.data.message;
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <CommissioningLayout
          sections={generateFormSectionsForScrollSpy({
            formSections: subSections,
            formSchema,
          })}
          submitProps={{
            isProcessing: mutationToSubmit.isPending,
            disabled: mutationToSubmit.isPending || hasChanges,
          }}
        />

        {formSchema.fixedInputs &&
          Object.entries(formSchema.fixedInputs).map(([name, value]) => {
            return (
              <input
                key={name}
                type="hidden"
                readOnly={true}
                {...register(name, {
                  value,
                })}
              />
            );
          })}

        <EnaFormFooter
          isDirty={isDirty}
          isSavingChanges={hasChanges}
          errorMessage={errorMessage}
        />
      </form>
    </FormProvider>
  );

  function getInitialFormValues() {
    const initialValues = structuredClone(
      connectionApplication.commissioning?.data ?? {}
    );
    let initialisationError: string | undefined = undefined;

    // The questions in the "devices" section need to be filled in for each
    // device in the original application. We prepopulate them here with the
    // system references from the original application.
    if (formSchema.properties?.devices?.items?.properties?.deviceSysRef) {
      const connectionApplicationData =
        ConnectionApplicationDataSchemaForCommissioning.safeParse(
          connectionApplication.data
        ).data;

      if (!connectionApplicationData) {
        initialisationError =
          'Unable to find devices from the original application. Please contact support.';
      } else {
        // Filter to only generation devices (those with generatorType)
        const generationDevices =
          connectionApplicationData.devicesToInstall.filter(
            (device) => device.generatorType != null
          );
        if (generationDevices.length === 0) {
          initialisationError =
            'This connection application has no generation devices and cannot be commissioned. Please contact support.';
        } else {
          const generationDeviceSysRefs = new Set(
            generationDevices.map((d) => d.deviceSysRef)
          );

          if (initialValues.devices) {
            // Filter existing saved devices to only keep generation devices
            initialValues.devices = (
              initialValues.devices as { deviceSysRef: string }[]
            ).filter((d) => generationDeviceSysRefs.has(d.deviceSysRef));
          } else {
            // Initialize from generation devices
            initialValues.devices = generationDevices.map((device) => ({
              deviceSysRef: device.deviceSysRef,
            }));
          }
        }
      }
    }
    return { initialValues, initialisationError };
  }

  function generateFormResolver(jsonSchema: JSONSchemaType<unknown>): Resolver {
    const jsonSchemaResolver = generateJsonSchemaResolver(jsonSchema);
    const edgeCasesResolver = zodResolver(
      z.object({
        files: z.object({
          projectFileIDs: z.array(z.string()),
        }),
      })
    ) as Resolver<any>;

    return async (values: any, ...args) => {
      const results = await Promise.all([
        jsonSchemaResolver(values, ...args),
        edgeCasesResolver(values, ...args),
      ]);
      return merge(...results);
    };
  }

  function onSubmit(data: unknown) {
    if (connectionApplication.commissioning) {
      mutationToSubmit.mutate({
        connectionApplicationPublicId: connectionApplication.id,
        data,
        form: {
          class: connectionApplication.commissioning.form.class,
          version: connectionApplication.commissioning.form.version,
          schema: connectionApplication.commissioning.form.schema,
        },
      });
    }
  }
};

export default CommissioningForm;
